<template>
  <transition name="game-pre-fade">
    <div v-show="visible" class="gpe-mask" @click.self="onMaskClick">
      <div class="gpe-dialog" @click.stop>
        <div v-if="entryLoading || entrySubmitting" class="gpe-loading" aria-live="polite">
          <span>{{ entrySubmitting ? '转入处理中…' : '余额加载中…' }}</span>
        </div>
        <h2 class="gpe-title">您即将进入{{ venueDisplayName }}</h2>
        <p class="gpe-sub">请选择转入资金或直接进入游戏</p>
        <div class="gpe-pill"><span class="gpe-pill-txt">{{ venueDisplayName }}余额: {{ displayVenueBalanceText }}</span></div>
        <div class="gpe-row gpe-row--input">
          <span class="gpe-yen">￥</span>
          <input v-model="transferAmount" class="gpe-input" type="text" inputmode="decimal" placeholder="请输入金额" :disabled="entrySubmitting" />
          <button type="button" class="gpe-btn-in" :disabled="entrySubmitting" @click="doTransferIn">转入</button>
        </div>
        <div class="gpe-row gpe-row--main">
          <span class="gpe-main-txt">主账户余额: <em class="gpe-main-num">{{ formatNum(mainBalance) }}</em></span>
          <a href="javascript:;" class="gpe-link" :class="{ 'gpe-link--disabled': entrySubmitting }" @click.prevent="transferAllAndEnter">全部转入</a>
        </div>
        <div class="gpe-footer-btns">
          <button type="button" class="gpe-btn-cancel" @click="onCancel">取消</button>
          <div class="gpe-btn-split" />
          <button type="button" class="gpe-btn-go" :disabled="entrySubmitting" @click="onEnterDirect">直接进入游戏</button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { rowsFromBalanceListResponse, findVenueRowInList, pickVenueBalance, formatBalanceValue } from '@/utils/balancelistHelper';
export default {
  name: 'GamePreEntryModal',
  props: {
    visible: { type: Boolean, default: false },
    platformName: { type: String, default: '' },
    gameType: { type: [String, Number], default: 0 },
    gameCode: { type: String, default: '' },
    venueTitle: { type: String, default: '' },
    /** 首页 /transfer 同源的 balancelist 余额，进弹窗先展示，再从接口刷新 */
    initialVenueBalance: { type: [String, Number], default: '' },
  },
  data() {
    return {
      entryLoading: false,
      entrySubmitting: false,
      transferAmount: '',
      venueBalance: '0.00',
      /** 来自 /api/balancelist 匹配项的 name（中文馆名等） */
      apiVenueName: '',
    };
  },
  computed: {
    mainBalance() {
      const u = this.$store.state.userInfo;
      return (u && u.balance) != null ? u.balance : '0.00';
    },
    hasInitialBalanceHint() {
      return this.initialVenueBalance != null && String(this.initialVenueBalance).trim() !== '';
    },
    /** 优先展示接口刷新后的余额；首页角标仅作打开瞬间的占位 */
    displayVenueBalanceText() {
      return formatBalanceValue(this.venueBalance);
    },
    /** 展示名：入口传入 > 接口钱包列表 > 平台代码 */
    venueDisplayName() {
      const t = (this.venueTitle && String(this.venueTitle).trim()) || '';
      if (t) {
        return t;
      }
      const api = (this.apiVenueName && String(this.apiVenueName).trim()) || '';
      if (api) {
        return api;
      }
      const p = (this.platformName && String(this.platformName).trim()) || '';
      return p || '游戏';
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(val) {
        if (!val) {
          return;
        }
        this.onOpenModal();
      },
    },
  },
  methods: {
    onOpenModal() {
      this.resetFields();
      if (this.hasInitialBalanceHint) {
        this.venueBalance = formatBalanceValue(this.initialVenueBalance);
      }
      this.$nextTick(() => {
        this.loadEntryBalances();
      });
    },
    resetFields() {
      this.transferAmount = '';
      this.venueBalance = '0.00';
      this.apiVenueName = '';
      this.entryLoading = false;
      this.entrySubmitting = false;
    },
    formatNum(v) {
      if (v === null || v === undefined || v === '') {
        return '0.00';
      }
      const n = parseFloat(String(v).replace(/,/g, ''));
      if (Number.isNaN(n)) {
        return '0.00';
      }
      return n.toFixed(2);
    },
    loadEntryBalances() {
      if (!this.platformName) {
        return;
      }
      this.entryLoading = true;
      this.$apiFun
        .post('/api/balancelist', {})
        .then(res => {
          if (res.code === 200) {
            const list = rowsFromBalanceListResponse(res);
            const hit = findVenueRowInList(list, this.platformName);
            if (hit) {
              this.apiVenueName = hit.name ? String(hit.name) : hit.game_name ? String(hit.game_name) : '';
              this.venueBalance = formatBalanceValue(pickVenueBalance(hit));
            } else {
              this.apiVenueName = '';
              this.venueBalance = '0.00';
            }
          } else if (this.$parent && this.$parent.showTost) {
            this.$parent.showTost(0, res.message || '获取场馆余额失败');
          }
        })
        .catch(() => {})
        .finally(() => {
          this.entryLoading = false;
        });
    },
    /** 全部转入：主账户有余额则转完进厅；无余额则直接进厅 */
    transferAllAndEnter() {
      if (this.entrySubmitting) {
        return;
      }
      const main = parseFloat(String(this.mainBalance).replace(/,/g, ''));
      if (Number.isNaN(main) || main <= 0) {
        this.$emit('proceed');
        return;
      }
      this.transferAmount = this.formatNum(main);
      this.doTransferIn();
    },
    onMaskClick() {
      this.onCancel();
    },
    onCancel() {
      this.$emit('close');
    },
    onEnterDirect() {
      if (this.entrySubmitting) {
        return;
      }
      this.$emit('proceed');
    },
    doTransferIn() {
      if (this.entrySubmitting) {
        return;
      }
      const amt = this.transferAmount;
      if (amt == null || String(amt).trim() === '') {
        this.$parent && this.$parent.showTost(0, '请输入操作金额！');
        return;
      }
      this.entrySubmitting = true;
      this.$parent && this.$parent.showLoading();
      const info = {
        amount: amt,
        sourcetype: 'userbalance',
        targettype: this.platformName,
      };
      this.$apiFun
        .post('/api/transfer', info)
        .then(res => {
          if (res.code === 200) {
            this.$parent && this.$parent.showTost(1, res.message || '转入成功');
            return this.refreshusermoney()
              .then(() => {
                this.$emit('proceed');
              })
              .catch(() => {
                this.$emit('proceed');
              });
          }
          this.$parent && this.$parent.showTost(0, res.message || '转入失败');
          this.$parent && this.$parent.hideLoading();
        })
        .catch(() => {
          this.$parent && this.$parent.showTost(0, '网络连接错误，请稍后再试');
          this.$parent && this.$parent.hideLoading();
        })
        .finally(() => {
          this.entrySubmitting = false;
        });
    },
    refreshusermoney() {
      return this.$apiFun.post('/api/refreshusermoney', {}).then(res => {
        if (res.code == 200 && res.data) {
          localStorage.setItem('userInfo', JSON.stringify(res.data));
          this.$store.commit('changUserInfo');
        }
        this.$parent && this.$parent.hideLoading();
      });
    },
  },
};
</script>

<style lang="scss" scoped>
/* 轻蒙层 + 毛玻璃：背后页面仍清晰可见 */
.gpe-mask {
  position: fixed;
  inset: 0;
  z-index: 10050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 20px;
  box-sizing: border-box;
  background: rgba(0, 0, 0, 0.2);
}
.game-pre-fade-enter-active,
.game-pre-fade-leave-active {
  transition: opacity 0.2s ease;
}
.game-pre-fade-enter,
.game-pre-fade-leave-to {
  opacity: 0;
}
.gpe-dialog {
  position: relative;
  width: 100%;
  max-width: 320px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 16px;
  overflow: hidden;
  box-sizing: border-box;
  padding: 20px 18px 0;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}
.gpe-loading {
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.35);
  border-radius: 16px;
  pointer-events: all;
  font-size: 14px;
  color: #555;
  font-weight: 500;
}
.gpe-title {
  position: relative;
  z-index: 1;
  margin: 0 0 8px;
  font-size: 17px;
  font-weight: 700;
  color: #1a1a1a;
  text-align: center;
  line-height: 1.35;
}
.gpe-sub,
.gpe-pill,
.gpe-row--input,
.gpe-row--main {
  position: relative;
  z-index: 1;
}
.gpe-sub {
  margin: 0 0 14px;
  font-size: 13px;
  color: #8c8c8c;
  text-align: center;
  line-height: 1.4;
}
.gpe-pill {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
}
.gpe-pill-txt {
  display: inline-block;
  padding: 8px 16px;
  background: #f0f2f5;
  border-radius: 999px;
  font-size: 13px;
  color: #5c6b8a;
}
.gpe-row--input {
  display: flex;
  align-items: center;
  border-bottom: 1px solid #e5e5e5;
  padding-bottom: 8px;
  margin-bottom: 14px;
}
.gpe-yen {
  font-size: 16px;
  color: #333;
  margin-right: 6px;
  flex-shrink: 0;
}
.gpe-input {
  flex: 1;
  border: 0;
  outline: 0;
  font-size: 16px;
  min-width: 0;
  background: transparent;
}
.gpe-btn-in {
  flex-shrink: 0;
  margin-left: 8px;
  padding: 6px 14px;
  font-size: 14px;
  color: #fff;
  background: #1989fa;
  border: 0;
  border-radius: 4px;
}
.gpe-btn-in:disabled {
  opacity: 0.5;
}
.gpe-row--main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  color: #333;
  margin-bottom: 4px;
}
.gpe-main-num {
  font-style: normal;
  color: #ee0a24;
  font-weight: 600;
}
.gpe-link {
  color: #1989fa;
  font-size: 14px;
}
.gpe-link--disabled {
  opacity: 0.4;
  pointer-events: none;
}
.gpe-footer-btns {
  position: relative;
  z-index: 1;
  display: flex;
  margin: 20px -18px 0;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}
.gpe-btn-split {
  width: 1px;
  background: rgba(0, 0, 0, 0.06);
  flex-shrink: 0;
}
.gpe-btn-cancel,
.gpe-btn-go {
  flex: 1;
  padding: 14px 8px;
  font-size: 16px;
  border: 0;
  background: rgba(255, 255, 255, 0.55);
  cursor: pointer;
}
.gpe-btn-cancel {
  color: #333;
}
.gpe-btn-go {
  color: #1989fa;
  font-weight: 500;
}
.gpe-btn-go:disabled {
  opacity: 0.4;
}
</style>



// WEBPACK FOOTER //
// src/components/GamePreEntryModal.vue